import ApiClient from './apiClient'
import { PreorderHardware } from './preorderHardwareService'
import { DedicatedHardware, DedicatedServerMin, DedicatedUserMin } from '../pages/dedicated-servers/types'

export type DedicatedPreorderStatus = 'submitted' | 'provisioning' | 'released' | 'cancelled'
export type PreorderCPU = Extract<PreorderHardware, { cores: number }>
export type PreorderRAM = Extract<PreorderHardware, { size_mb: number }>
export type PreorderDisk = Extract<PreorderHardware, { size_gb: number }>

export type DedicatedPreorder = {
  id: string;
  name: string;
  status: DedicatedPreorderStatus;
  dedicated_user: DedicatedUserMin | null;
  cpu: PreorderCPU | null;
  cpu_count: number;
  ram: PreorderRAM | null;
  ram_count: number;
  disks: { id: string; disk: PreorderDisk | null }[];
  hardware: DedicatedHardware | null;
  issued_server: DedicatedServerMin | null;
  submitted_at: string | null;
  provisioning_at: string | null;
  released_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

export type DedicatedPreorderDetail = DedicatedPreorder & {
  hostname: string;
  os_id: number | null;
  ssh_keys: unknown;
  comment: string;
  staff_comment: string;
  issued_server_external_id: number | null;
}

export type DedicatedPreorderUpdate = {
  status?: DedicatedPreorderStatus;
  cpu?: string;
  cpu_count?: number;
  ram?: string;
  ram_count?: number;
  disk_ids?: string[];
  comment?: string;
  staff_comment?: string;
}

export type DedicatedPreordersQuery = {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
  status?: string;
  dedicated_user?: string;
  issued_server?: string;
}

type PreordersResponse = {
  count?: number;
  totalCount?: number;
  next?: string | null;
  previous?: string | null;
  objects?: DedicatedPreorder[];
  results?: DedicatedPreorder[];
  permissions?: Record<string, boolean>;
}

export type DedicatedPreordersList = {
  count: number;
  next: string | null;
  previous: string | null;
  objects: DedicatedPreorder[];
}

const base = 'dedicated-servers/preorders'

const dedicatedPreordersService = {
  list: async (params: DedicatedPreordersQuery, signal?: AbortSignal): Promise<DedicatedPreordersList> => {
    const { data } = await ApiClient.get<PreordersResponse>(base, { params, signal })
    const objects = data.objects ?? data.results ?? []
    return { count: data.count ?? data.totalCount ?? objects.length, next: data.next ?? null, previous: data.previous ?? null, objects }
  },
  get: async (id: string, signal?: AbortSignal) => (
    await ApiClient.get<DedicatedPreorderDetail>(`${base}/${id}`, { signal })
  ).data,
  update: (id: string, data: DedicatedPreorderUpdate) => ApiClient.patch(`${base}/${id}`, data),
  release: async (id: string, serverId: string) => (
    await ApiClient.post<{ operation_id: string }>(`${base}/${id}/release`, { server_id: serverId })
  ).data,
}

export default dedicatedPreordersService
